import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Draft } from '@app/modules/documents/store/models/draft.state.model';
import { environment, PRESSURL } from '@env/environment';
import { AuthStateService } from '@app/modules/login-form/state/auth/auth.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  CedroSendDossierRequest,
  CedroSendDossierResponse,
  CedroTokenRequest,
  CedroTokenResponse,
  CedroUserAccessRequest,
  CedroUserAccessResponse,
} from '@app/models/press.model';

@Injectable({
  providedIn: 'root',
})
export class DraftService {
  static readonly url = PRESSURL;

  cedroTokenFlowData$: BehaviorSubject<CedroTokenResponse> = new BehaviorSubject(null);

  constructor(private http: HttpClient, private _authStateService: AuthStateService) {}

  newDraft(payload: Draft) {
    return this.http.post(`${DraftService.url}dossiers/${payload.dossier_id}/drafts`, payload);
  }

  getDraft(payload: { dossier_id: number; draft_id: number }) {
    return this.http.get(`${DraftService.url}dossiers/${payload.dossier_id}/drafts/${payload.draft_id}`);
  }

  editDraft(payload: Draft) {
    return this.http.put(`${DraftService.url}dossiers/${payload.dossier_id}/drafts/${payload.id}`, payload);
  }

  deleteDraft(payload: { dossier_id: number; draft_id: number }) {
    return this.http.delete(`${DraftService.url}dossiers/${payload.dossier_id}/drafts/${payload.draft_id}`);
  }

  generateDraftPdf(payload: {
    dossier_id: number;
    draft_id: number;
    covers_date: any;
    position_covers_date: 'start' | 'end';
    send_pdf: boolean;
    send_clipping: boolean;
  }) {
    const post = {
      covers: payload.covers_date ? payload.position_covers_date : '', // '', 'star', 'end']
      covers_date: payload.covers_date,
      empty_category: false,
      toc: true,
      templates: {
        cover_template: this._authStateService.userConfig.interface_config.cover_template,
        multimedia_template: this._authStateService.userConfig.interface_config.multimedia_template,
        digital_template: this._authStateService.userConfig.interface_config.digital_template,
        search_template: this._authStateService.userConfig.interface_config.search_template,
        legal_notice: this._authStateService.userConfig.interface_config.legal_notice,
        index_template: this._authStateService.userConfig.interface_config.index_template,
        header_template: this._authStateService.userConfig.interface_config.header_template,
      },
      recipients: [this._authStateService.userConfig.email],
      mail_sender: this._authStateService.userConfig.interface_config.mail_sender,
      referer_host: window.location.href,
      send_pdf: payload.send_pdf,
      send_clipping: payload.send_clipping,
    };

    return this.http.post(`${DraftService.url}dossiers/${payload.dossier_id}/drafts/${payload.draft_id}/send`, post);
  }

  getCedroToken(payload: CedroTokenRequest): Observable<CedroTokenResponse> {
    return this.http.get<CedroTokenResponse>(`${environment.clippingCedroToken}`, payload);
  }

  cedroUserAccess(payload: CedroUserAccessRequest): Observable<CedroUserAccessResponse> {
    return this.http.post<CedroUserAccessResponse>(`${environment.clippingCedroUserAccess}${payload.token}`, payload);
  }

  cedroSendDossier(payload: CedroSendDossierRequest): Observable<CedroSendDossierResponse> {
    return this.http.post<CedroUserAccessResponse>(`${environment.clippingCedroSendDossier}${payload.token}`, payload);
  }

  getVisorToken(): Observable<String> {
    return of(
      'Bearer eyJhbGciOiJSUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICJhTW9aZ0RhN0RQcU92cG9KeDBzZ2ZIUFFPbUJfR0lQbkduQ25WcElORW9BIn0.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.Zt5nRVLWZGDXj-v7OvfffAyoP9nihJ4IZaEfPjQofdXasLN9suiCcoMWz3CkIuEduRlAi_pmYsen5zThMT9i4KXzzJOuH-PWnwVmkABzZaAMePIADsn07GICx6OLJ9uYVC5uAh2-PcrGynAD1maRg8pC5yJlPmv0vkin1qqlpjJfDrj_PN64n0F1weTdpdpQob2YAL2eWv1Zcs584kNlQdlDQPX96eRnNmqJXZDf9VULpHKmxrcZeuYfpUBJfy3oAfVlHsvd0I5-JDec6cEDoxhWV7n_qOvAmJwi_5nKPhEfghZ8TuUeEldznVfECTT0cuE3Wv1U1MfxLRvXDA8BJg'
    );
  }
}
